import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Stimulus controller for the Dynamic Import multi-file upload drop zone.
 *
 * Drives a drag/drop + click-to-browse zone backed by a hidden
 * `<input type="file" multiple>`. Maintains its own `selectedFiles`
 * array so that users can remove individual files before submitting.
 * On form submit, the hidden input is repopulated via a `DataTransfer`
 * so standard multipart form semantics (`import_run[files][]`) apply.
 *
 * Client-side filters:
 *  - accepted extensions (configurable, e.g. .csv,.xlsx)
 *  - per-file size cap (bytes)
 *  - per-run file count cap
 *  - duplicate filename rejection (case-insensitive)
 *
 * Server validation in `Organizations::Imports::CreateImportRun`
 * mirrors the same rules for defense-in-depth.
 */
export default class ImportFileDropController extends Controller<HTMLElement> {
  static targets = ["dropZone", "fileInput", "fileList", "errorContainer"]
  static values = {
    maxFiles: { type: Number, default: 20 },
    maxFileBytes: { type: Number, default: 200 * 1024 * 1024 },
    acceptedExtensions: { type: String, default: ".csv,.xlsx" },
  }

  declare readonly dropZoneTarget: HTMLElement
  declare readonly fileInputTarget: HTMLInputElement
  declare readonly fileListTarget: HTMLUListElement
  declare readonly errorContainerTarget: HTMLElement
  declare readonly hasErrorContainerTarget: boolean

  declare maxFilesValue: number
  declare maxFileBytesValue: number
  declare acceptedExtensionsValue: string

  private selectedFiles: File[] = []
  private boundFormSubmit?: (event: Event) => void

  connect(): void {
    this.selectedFiles = []
    this.render()

    const form = this.element.closest("form")
    if (form) {
      this.boundFormSubmit = this.syncFileInput.bind(this)
      form.addEventListener("submit", this.boundFormSubmit, { capture: true })
    }
  }

  disconnect(): void {
    const form = this.element.closest("form")
    if (form && this.boundFormSubmit) {
      form.removeEventListener("submit", this.boundFormSubmit, { capture: true })
    }
  }

  // --- Actions -------------------------------------------------------------

  openPicker(event: Event): void {
    // Avoid re-opening when the inner hidden input fires its own click.
    if ((event.target as HTMLElement).tagName === "INPUT") return
    this.fileInputTarget.click()
  }

  keyActivate(event: KeyboardEvent): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.fileInputTarget.click()
    }
  }

  dragOver(event: DragEvent): void {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-primary", "bg-base-200")
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-primary", "bg-base-200")
  }

  drop(event: DragEvent): void {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-primary", "bg-base-200")
    const dropped = Array.from(event.dataTransfer?.files || [])
    this.addFiles(dropped)
  }

  filesSelected(event: Event): void {
    const input = event.currentTarget as HTMLInputElement
    const picked = Array.from(input.files || [])
    this.addFiles(picked)
  }

  remove(event: Event): void {
    event.preventDefault()
    const button = event.currentTarget as HTMLButtonElement
    const index = Number(button.dataset.index)
    if (Number.isNaN(index)) return

    this.selectedFiles.splice(index, 1)
    this.clearError()
    this.render()
    this.syncFileInput()
  }

  // --- Helpers -------------------------------------------------------------

  private addFiles(candidates: File[]): void {
    this.clearError()

    for (const file of candidates) {
      if (!this.hasAllowedExtension(file)) {
        this.showError(
          i18n.t("import_file_drop.invalid_extension", { filename: file.name })
        )
        return
      }

      if (file.size > this.maxFileBytesValue) {
        this.showError(
          i18n.t("import_file_drop.file_too_large", {
            filename: file.name,
            max_mb: Math.round(this.maxFileBytesValue / (1024 * 1024)),
          })
        )
        return
      }

      if (this.isDuplicate(file)) {
        this.showError(
          i18n.t("import_file_drop.duplicate_filename", { filename: file.name })
        )
        return
      }

      if (this.selectedFiles.length >= this.maxFilesValue) {
        this.showError(
          i18n.t("import_file_drop.too_many_files", { max: this.maxFilesValue })
        )
        return
      }

      this.selectedFiles.push(file)
    }

    this.render()
    this.syncFileInput()
  }

  private hasAllowedExtension(file: File): boolean {
    const allowed = this.acceptedExtensionsValue
      .split(",")
      .map((ext) => ext.trim().toLowerCase())
    const lower = file.name.toLowerCase()
    return allowed.some((ext) => ext && lower.endsWith(ext))
  }

  private isDuplicate(file: File): boolean {
    const lower = file.name.toLowerCase()
    return this.selectedFiles.some((f) => f.name.toLowerCase() === lower)
  }

  private render(): void {
    this.fileListTarget.innerHTML = ""

    if (this.selectedFiles.length === 0) {
      this.fileListTarget.classList.add("hidden")
      return
    }

    this.fileListTarget.classList.remove("hidden")

    this.selectedFiles.forEach((file, index) => {
      const li = document.createElement("li")
      li.className = "flex items-center justify-between gap-2 py-2 px-3"

      const name = document.createElement("span")
      name.className = "truncate text-sm"
      name.textContent = file.name
      li.appendChild(name)

      const button = document.createElement("button")
      button.type = "button"
      button.className = "btn btn-ghost btn-xs"
      button.dataset.index = String(index)
      button.dataset.action = "click->import-file-drop#remove"
      button.setAttribute("aria-label", i18n.t("import_file_drop.remove"))
      button.textContent = i18n.t("import_file_drop.remove")
      li.appendChild(button)

      this.fileListTarget.appendChild(li)
    })
  }

  // Rehydrate the hidden file input from the controller's internal list so
  // that a plain multipart form submit sends exactly the files the user sees.
  private syncFileInput(): void {
    const dt = new DataTransfer()
    this.selectedFiles.forEach((file) => dt.items.add(file))
    this.fileInputTarget.files = dt.files
  }

  private showError(message: string): void {
    if (!this.hasErrorContainerTarget) return
    this.errorContainerTarget.textContent = message
    this.errorContainerTarget.classList.remove("hidden")
  }

  private clearError(): void {
    if (!this.hasErrorContainerTarget) return
    this.errorContainerTarget.textContent = ""
    this.errorContainerTarget.classList.add("hidden")
  }
}
